/**
 * Serialization for the two internet-shortcut formats, plus which one this
 * machine wants.
 *
 * Deliberately zero-import and side-effect-free apart from `detectShortcutFormat`,
 * which is the only function here that reads the environment.
 */

export type ShortcutFormat = 'url' | 'webloc';

/** Stamped into every file so a support conversation can identify our output. */
const GENERATED_BY = 'matic-pages';

export function shortcutFileExtension(format: ShortcutFormat): string {
  return format === 'url' ? '.url' : '.webloc';
}

/**
 * Windows wants `.url`, everything else `.webloc`. Detection only — the export
 * UI states which format it's about to write, and offers an override, because
 * a Mac user writing into a folder shared with Windows colleagues is a real
 * case that detection gets wrong.
 */
export function detectShortcutFormat(): ShortcutFormat {
  const platform =
    (globalThis.navigator as {userAgentData?: {platform?: string}} | undefined)
      ?.userAgentData?.platform ??
    // `navigator.platform` is deprecated but is the only fallback that works
    // when User-Agent Client Hints are unavailable. Reached rarely, since
    // `userAgentData` is present in exactly the browsers this feature supports.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    globalThis.navigator?.platform ??
    '';

  return platform.toLowerCase().includes('win') ? 'url' : 'webloc';
}

/**
 * XML-escape for the `.webloc` plist body.
 *
 * This is the single highest-risk line in the feature. Every multi-param
 * Library URL contains `&`; an unescaped one makes the plist unparseable and
 * Finder renders a dead generic file rather than a shortcut. `&` must be
 * replaced first or it would double-escape the entities added after it.
 */
function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/**
 * Windows Internet Shortcut: an INI file, CRLF-terminated, no BOM. The shell
 * ignores sections it doesn't recognize, so the ownership marker is safe.
 *
 * No `IconFile` — it needs an absolute path to a local `.ico`, and
 * `showDirectoryPicker` hands back a handle rather than a path, so we never
 * learn where the folder actually is. Shortcuts show the browser's icon.
 */
function renderUrlFile(url: string): string {
  return [
    '[InternetShortcut]',
    `URL=${url}`,
    '',
    '[MATIC]',
    `GeneratedBy=${GENERATED_BY}`,
    '',
  ].join('\r\n');
}

/**
 * The macOS bookmark format: a data-fork XML property list, UTF-8, LF, no BOM.
 * (The resource-fork variant is legacy; modern Finder reads this one.) Unknown
 * dict keys are ignored, so the ownership marker rides along here too.
 */
function renderWeblocFile(url: string): string {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">',
    '<plist version="1.0">',
    '<dict>',
    '\t<key>URL</key>',
    `\t<string>${escapeXml(url)}</string>`,
    '\t<key>MATICGeneratedBy</key>',
    `\t<string>${GENERATED_BY}</string>`,
    '</dict>',
    '</plist>',
    '',
  ].join('\n');
}

export function renderShortcutFile(
  url: string,
  format: ShortcutFormat
): string {
  return format === 'url' ? renderUrlFile(url) : renderWeblocFile(url);
}

/**
 * Whether file content was written by this feature.
 *
 * Checked before any delete during folder pruning — a file (or the whole
 * folder) can be shared with something the user placed there themselves, and
 * `removeEntry` on the File System Access API does not go through the OS
 * trash. "Not in the current manifest" is not sufficient grounds to delete;
 * "not one of ours" is required too.
 */
export function isMaticShortcutFile(contents: string): boolean {
  return contents.includes(GENERATED_BY);
}
